
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getConversations, archiveConversation, unarchiveConversation, deleteConversation } from '@/api/services/copilotHistoryService';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { setCurrentConversation } from '@/store/slices/copilot/copilotChatSlice';
import { setActiveTab } from '@/store/slices/copilot/copilotSlice';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  MessageSquare, 
  Search, 
  Archive, 
  Trash2,
  Paperclip,
  File,
  Calendar
} from 'lucide-react';
import { format, isToday, isYesterday, startOfWeek, endOfWeek, isWithinInterval, subWeeks } from 'date-fns';
import { Input } from '@/components/ui/input';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';
import { ChatConversation } from '@/types/copilot-chat';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

interface ConversationHistoryListProps {
  activeConversationId: string;
  showArchived?: boolean;
  onToggleArchived?: () => void;
}

// Define date categories for grouping
type DateCategory = 'Today' | 'Yesterday' | 'This Week' | 'Last Week' | 'Earlier';

export const ConversationHistoryList = ({ 
  activeConversationId,
  showArchived = false,
  onToggleArchived
}: ConversationHistoryListProps) => {
  
  const dispatch = useAppDispatch();
  const { getSessionToken } = useAuth();
  
  // Local state for conversations
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [conversationToDelete, setConversationToDelete] = React.useState<string | null>(null);
  
  // Load conversations
  const loadConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getConversations(
        {
          page,
          limit: 30,
          search: searchTerm || undefined,
          showArchived
        },
        getSessionToken
      );
      
      const allConversations = response.data || [];
      const filteredConversations = allConversations.filter(conversation =>
        showArchived ? conversation.status === 'archived' : conversation.status !== 'archived'
      );

      setConversations(filteredConversations);

      const limit = response.pagination?.limit || filteredConversations.length || 1;
      const computedTotalPages = response.pagination?.totalPages
        ? response.pagination.totalPages
        : Math.max(1, Math.ceil(filteredConversations.length / limit));
      setTotalPages(computedTotalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, showArchived, getSessionToken]);
  
  // Load conversations on mount and when dependencies change
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);
  
  // Handle archive toggle
  const handleArchiveToggle = async (conversation: ChatConversation) => {
    try {
      if (showArchived) {
        await unarchiveConversation(conversation.id, getSessionToken);
      } else {
        await archiveConversation(conversation.id, getSessionToken);
      }
      // Reload conversations
      await loadConversations();
    } catch (err: any) {
      setError(err.message || 'Failed to update conversation');
    }
  };
  
  // Handle delete
  const handleDelete = async (conversationId: string) => {
    try {
      await deleteConversation(conversationId, getSessionToken);
      // Reload conversations
      await loadConversations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete conversation');
    }
  };
  
  // Handle conversation selection
  const handleSelectConversation = (conversationId: string) => {
    const normalized = conversationId?.trim?.() ?? '';
    dispatch(setCurrentConversation(normalized || null));
    dispatch(setActiveTab('chat'));
  };
  
  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  // Group conversations by date category
  const groupedConversations = useMemo(() => {
    // Define the date categories
    const categories: Record<DateCategory, ChatConversation[]> = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Last Week': [],
      'Earlier': []
    };
    
    // Sort conversations by date (newest first) - use last_message_at if available
    const sortedConversations = [...conversations].sort((a, b) => {
      const aDate = a.last_message_at || a.updatedAt || a.createdAt;
      const bDate = b.last_message_at || b.updatedAt || b.createdAt;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
    
    // Categorize each conversation
    sortedConversations.forEach(conversation => {
      const date = new Date(conversation.last_message_at || conversation.updatedAt || conversation.createdAt);
      
      if (isToday(date)) {
        categories['Today'].push(conversation);
      } else if (isYesterday(date)) {
        categories['Yesterday'].push(conversation);
      } else {
        const now = new Date();
        const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
        const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        
        if (isWithinInterval(date, { start: thisWeekStart, end: thisWeekEnd })) {
          categories['This Week'].push(conversation);
        } else if (isWithinInterval(date, { start: lastWeekStart, end: lastWeekEnd })) {
          categories['Last Week'].push(conversation);
        } else {
          categories['Earlier'].push(conversation);
        }
      }
    });
    
    return categories;
  }, [conversations]);
  
  const emptyStateMessage = useMemo(() => {
    if (searchTerm) {
      return {
        title: "No matching conversations",
        description: "Try a different search term or clear your filters"
      };
    }
    
    if (showArchived) {
      return {
        title: "No archived conversations",
        description: "When you archive conversations, they will appear here"
      };
    }
    
    return {
      title: "No conversations yet",
      description: "Start chatting with Pullse Copilot to get help and insights about your data"
    };
  }, [searchTerm, showArchived]);
  
  // Show loading state
  if (loading && conversations.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4"
              disabled={loading}
            />
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-4">
          <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          <h3 className="text-lg font-medium text-foreground/80">Loading conversations...</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Please wait while we fetch your conversation history
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-4">
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <MessageSquare className="h-8 w-8 text-destructive/60" />
          </div>
          <h3 className="text-lg font-medium text-foreground/80">Failed to load conversations</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            {error}
          </p>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!conversations || conversations.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-4">
          <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <MessageSquare className="h-8 w-8 text-muted-foreground/60" />
          </div>
          <h3 className="text-lg font-medium text-foreground/80">{emptyStateMessage.title}</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            {emptyStateMessage.description}
          </p>
        </div>
      </div>
    );
  }
  
  // Check if there are any conversations in the categories
  const hasConversations = Object.values(groupedConversations).some(group => group.length > 0);
  
  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center px-6 py-3 bg-muted/10 border-b border-border/20">
        <h3 className="text-sm font-medium text-foreground">
          {showArchived ? 'Archived Conversations' : 'Conversation History'}
        </h3>
        {onToggleArchived && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onToggleArchived}
            className="text-xs h-8 px-3 rounded-lg"
          >
            {showArchived ? 'Show Active' : 'Show Archived'}
          </Button>
        )}
      </div>
      <div className="p-6 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4"
          />
        </div>
      </div>

      <ScrollArea className="flex-1 px-6 py-4">
        <h3 className="text-sm font-medium px-2 mb-4 text-foreground/80">
          {showArchived ? 'Archived conversations' : 'Conversations history'}
        </h3>
        
        <div className="space-y-6">
          {hasConversations ? (
            Object.entries(groupedConversations).map(([category, categoryConversations]) => (
              categoryConversations.length > 0 && (
                <div key={category} className="space-y-2">
                  <div className="flex items-center gap-2 px-2 py-1">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{category}</h4>
                  </div>
                  
                  <AnimatePresence initial={false}>
                    {categoryConversations.map((conversation) => (
                      <ConversationCard
                        key={conversation.id}
                        conversation={conversation}
                        isActive={activeConversationId === conversation.id}
                        onSelect={() => handleSelectConversation(conversation.id)}
                        onArchiveToggle={() => handleArchiveToggle(conversation)}
                        onDelete={() => setConversationToDelete(conversation.id)}
                        isArchived={showArchived}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )
            ))
          ) : (
            <div className="flex flex-col items-center justify-center text-center p-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <MessageSquare className="h-8 w-8 text-muted-foreground/60" />
              </div>
              <h3 className="text-lg font-medium text-foreground/80">{emptyStateMessage.title}</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                {emptyStateMessage.description}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
      
      {totalPages > 1 && (
        <div className="border-t p-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(page - 1)}
                  className={cn(page <= 1 && "pointer-events-none opacity-50")}
                />
              </PaginationItem>
              
              {/* Generate page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => Math.abs(p - page) < 2 || p === 1 || p === totalPages)
                .map((p, i, arr) => {
                  // Add ellipsis if there are gaps in sequence
                  const showEllipsisBefore = i > 0 && p > arr[i - 1] + 1;
                  
                  return (
                    <React.Fragment key={p}>
                      {showEllipsisBefore && (
                        <PaginationItem>
                          <div className="px-4 py-2">...</div>
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => handlePageChange(p)}
                          isActive={page === p}
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    </React.Fragment>
                  );
                })
              }
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(page + 1)} 
                  className={cn(page >= totalPages && "pointer-events-none opacity-50")}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
      
      <AlertDialog 
        open={!!conversationToDelete} 
        onOpenChange={(open) => {
          if (!open) setConversationToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and its messages. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (conversationToDelete) {
                  handleDelete(conversationToDelete);
                  setConversationToDelete(null);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface ConversationCardProps {
  conversation: ChatConversation;
  isActive: boolean;
  isArchived?: boolean;
  onSelect: () => void;
  onArchiveToggle: () => void;
  onDelete: () => void;
}

const ConversationCard = ({
  conversation,
  isActive,
  isArchived = false,
  onSelect,
  onArchiveToggle,
  onDelete
}: ConversationCardProps) => {
  const lastMessage = conversation.messages && conversation.messages.length > 0 
    ? conversation.messages[conversation.messages.length - 1] 
    : null;
    
  const formattedDate = lastMessage 
    ? format(new Date(lastMessage.timestamp || lastMessage.createdAt), 'h:mm a')
    : conversation.last_message_at 
      ? format(new Date(conversation.last_message_at), 'h:mm a')
      : format(new Date(conversation.createdAt), 'h:mm a');
  
  // Count different message types
  const messageStats = useMemo(() => {
    let actionCards = 0;
    let hasAttachments = false;
    
    // Use messages array if available (for detailed stats), otherwise just use message_count
    if (conversation.messages?.length) {
      conversation.messages.forEach(msg => {
        if (msg.type === 'action_card') {
          actionCards++;
        } else if (msg.type === 'message' && msg.attachments?.length) {
          hasAttachments = true;
        }
      });
    }
    
    return {
      total: conversation.message_count || conversation.messages?.length || 0,
      actionCards,
      hasAttachments
    };
  }, [conversation.message_count, conversation.messages]);
  
  let previewContent = "";
  let contentType: 'text' | 'action_card' | 'attachment' = 'text';
  
  if (lastMessage) {
    if (lastMessage.type === 'message') {
      previewContent = lastMessage.content;
      contentType = 'text';
      
      // If message has attachments, prioritize showing that
      if (lastMessage.attachments?.length) {
        contentType = 'attachment';
        previewContent = `Attachment: ${lastMessage.attachments[0].filename || 'File'}`;
      }
    } else if (lastMessage.type === 'action_card') {
      contentType = 'action_card';
      previewContent = `${lastMessage.title} - ${lastMessage.summary}`;
    }
  }
  
  const truncatedPreview = previewContent && previewContent.length > 60
    ? `${previewContent.substring(0, 60)}...`
    : previewContent;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ scale: 1.01 }}
      className="relative group"
    >
      <Button
        variant="ghost"
        className={cn(
          "w-full justify-start text-left px-4 py-3 h-auto",
          "rounded-lg border",
          "transition-all duration-300",
          "hover:bg-muted/50",
          "group relative",
          isActive 
            ? "bg-primary/5 border-primary/20" 
            : "bg-transparent border-transparent"
        )}
        onClick={onSelect}
      >
        <div className="flex flex-col w-full gap-1">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-foreground/90 line-clamp-1">
              {conversation.title}
            </h4>
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formattedDate}
            </span>
          </div>
          
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {contentType === 'action_card' && (
              <Badge variant="outline" className="h-4 px-1 text-[9px] bg-muted/50 flex items-center gap-1">
                <File className="h-2.5 w-2.5" />
                Action
              </Badge>
            )}
            
            {contentType === 'attachment' && (
              <Badge variant="outline" className="h-4 px-1 text-[9px] bg-muted/50 flex items-center gap-1">
                <Paperclip className="h-2.5 w-2.5" />
                Attachment
              </Badge>
            )}
          </div>
          
          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
            {truncatedPreview}
          </p>
          
          <div className="flex items-center gap-2 mt-1.5">
            <Badge className="text-[10px] h-5 bg-muted/80 hover:bg-muted text-muted-foreground">
              {messageStats.total} {messageStats.total === 1 ? 'message' : 'messages'}
            </Badge>
            
            {messageStats.actionCards > 0 && (
              <Badge variant="outline" className="text-[10px] h-5 border-muted-foreground/20">
                {messageStats.actionCards} action{messageStats.actionCards !== 1 && 's'}
              </Badge>
            )}
            
            {messageStats.hasAttachments && (
              <Badge variant="outline" className="text-[10px] h-5 border-muted-foreground/20 flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
              </Badge>
            )}
          </div>
        </div>
      </Button>
      
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <span className="sr-only">Open menu</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <circle cx="12" cy="5" r="1" />
                <circle cx="12" cy="12" r="1" />
                <circle cx="12" cy="19" r="1" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              onArchiveToggle();
            }}>
              <Archive className="mr-2 h-4 w-4" />
              {isArchived ? 'Unarchive' : 'Archive'}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </motion.div>
  );
};
